/**
 * The expense programme's charge, savings and payback as the strip and its drawer print them (DESIGN §7). The figures are the engine's `PreviewResult.costProgramme` (ADR-0077); where the result does not
 * carry it every cell prints "–" and nothing is estimated here.
 */
import type { PreviewResult } from "@bsim/domain";
import { formatUsdM, formatUsdPerShare } from "../../format";

export type CostProgramme = NonNullable<PreviewResult["costProgramme"]>;
export const costProgrammeOf = (result: PreviewResult | null | undefined): CostProgramme | undefined => result?.costProgramme;

/** The expense options that start a programme: a cut or an investment; "hold" is the standing policy. */
export const isProgramme = (value: unknown): boolean => typeof value === "string" && value !== "hold";

const dash = "–";
export const GROSS_LABEL = "Saves (gross)";
export const NET_LABEL = "Net of lost fees and deposits";
export const NO_PAYBACK = "Does not pay back within three years.";
export const LOOK_THROUGH_CLAUSE = "The market looks through one-off charges, so the price counts only the capital they use, not the charge as lost earnings.";

export interface Strip { readonly charge: string; readonly years: readonly string[]; readonly netYears: readonly string[] | null; readonly payback: string; readonly capital: string }
export function stripOf(p: CostProgramme | undefined): Strip {
  const money = (x: number | undefined): string => (x === undefined ? dash : formatUsdM(x));
  return {
    charge: money(p?.chargeUsd),
    years: [0, 1, 2].map((i) => money(p?.savingsByYearUsd[i])),
    // The payback is computed on the net row, so the net row is printed beside the gross one (older results without it print the gross row alone).
    netYears: p?.netSavingsByYearUsd === undefined ? null : [0, 1, 2].map((i) => money(p.netSavingsByYearUsd?.[i])),
    payback: p === undefined ? dash : p.paybackYears === null ? NO_PAYBACK : `${(Math.round(p.paybackYears * 10) / 10).toFixed(1)} years`,
    capital: p === undefined ? "It also uses capital, once, after tax." : `It also uses about ${Math.round(p.capitalHitBp)} bp of CET1 (${formatUsdPerShare(p.capitalHitUsdPerShare)} a share, after tax), once.`,
  };
}

const TENTH_M = 1e5;
export interface ProgrammeRow { readonly id: "charge" | "gross" | "lost" | "net" | "cumulative"; readonly label: string; /** Now, year 1, 2, 3: null where the row has no figure for the column. */ readonly cells: readonly (number | null)[]; /** The row's three-year total in the same units; null for a running balance, whose last cell is its own total. */ readonly total: number | null }
export const PROGRAMME_COLUMNS = ["Now", "Year 1", "Year 2", "Year 3", "Three years"] as const;

/**
 * The whole programme as one table that adds up in the figures it prints (tenths of $1M, in integers): the charge now, the gross saving each year, what the fee and deposit losses take off (net less gross), the net saving, and the
 * running total after the charge (the row the payback is read on). Every figure is the engine's (`costProgramme`); the lost row is the net row less the gross row, and the running total is the charge plus the net rows, so each column and each row sums as printed.
 * Without the net row (an older result) the running total reads the gross row and the lost and net rows are left out. Values are whole dollars at a tenth of $1M.
 */
export function programmeTable(p: CostProgramme): readonly ProgrammeRow[] {
  const u = (x: number): number => Math.round(x / TENTH_M);
  const gross = p.savingsByYearUsd.map(u), net = p.netSavingsByYearUsd?.map(u), charge = u(p.chargeUsd);
  const years = net ?? gross;
  const running = years.reduce<number[]>((acc, y) => [...acc, (acc.at(-1) ?? -charge) + y], [-charge]);
  const sum = (xs: readonly number[]): number => xs.reduce((t, x) => t + x, 0);
  const usd = (x: number): number => x * TENTH_M;
  const row = (id: ProgrammeRow["id"], label: string, cells: readonly (number | null)[], total: number | null): ProgrammeRow => ({ id, label, cells: cells.map((c) => (c === null ? null : usd(c))), total: total === null ? null : usd(total) });
  return [
    row("charge", "One-off charge, before tax", [-charge, null, null, null], -charge),
    row("gross", GROSS_LABEL, [null, ...gross], sum(gross)),
    ...(net === undefined ? [] : [row("lost", "Fees and deposit margin lost", [null, ...net.map((n, i) => n - (gross[i] ?? 0))], sum(net) - sum(gross)), row("net", NET_LABEL, [null, ...net], sum(net))]),
    row("cumulative", "Running total after the charge", running, null),
  ];
}

/** The programme's other figures, in the words the strip uses: payback, the charge after tax and the capital it uses, the deposits lost, and the engine's own look-through sentence. */
export function programmeFacts(p: CostProgramme): readonly { readonly id: string; readonly label: string; readonly value: string }[] {
  const s = stripOf(p);
  return [
    { id: "payback", label: "Pays back in", value: s.payback },
    { id: "aftertax", label: "The charge after tax", value: formatUsdM(p.chargeAfterTaxUsd) },
    { id: "capital", label: "Capital it uses, once", value: `${Math.round(p.capitalHitBp)} bp of CET1 (${formatUsdPerShare(p.capitalHitUsdPerShare)} a share)` },
    { id: "deposits", label: p.serviceDepositLossUsd < 0 ? "Deposits gained by year 3" : "Deposits lost by year 3", value: formatUsdM(Math.abs(p.serviceDepositLossUsd)) },
  ];
}
